// Lógica de la hoja de hospitalización (pura, sin React)
import { FARMACOS, presentaciones, idFarmaco } from '../vademecum/farmacos.js';

export const H = 36e5;
export const RANGOS = {
  perro: { temp: [37.5, 39.2], fc: [60, 140], fr: [10, 30], pas: [90, 160], glucemia: [70, 140], spo2: [95, 100] },
  gato: { temp: [37.8, 39.2], fc: [140, 220], fr: [20, 30], pas: [90, 160], glucemia: [70, 160], spo2: [95, 100] },
};
export const ESTADOS_PAC = {
  critico: ['Crítico', '#b91c1c'], vigilancia: ['Vigilancia', '#b45309'], estable: ['Estable', '#15803d'], alta: ['Alta prevista', '#2563eb'],
};
export const FLAGS = { npo: '🚫🍽 Ayunas', agresivo: '⚠️ Muerde', infeccioso: '☣️ Aislamiento', alergias: '💊 Alergias', dnr: '⛔ No reanimar' };

// ---------- Validación de peso / dosis / fluidos (seguridad clínica) ----------
// Rangos documentados: no se sustituyen valores incorrectos; el caller muestra el error.
// Perro 0,1–150 kg (cachorro pequeño → raza gigante con margen).
// Gato 0,05–25 kg. Otros / desconocido: 0,01–150 kg (tope global).
export const PESO_RANGOS = {
  perro: { min: 0.1, max: 150 },
  gato: { min: 0.05, max: 25 },
  otro: { min: 0.01, max: 150 },
};
// Límites para entradas del usuario (no modifican el vademécum).
export const LIMITES_HOSP = {
  mgkg: { min: 0.001, max: 500, etiqueta: 'mg/kg' },
  conc: { min: 0.001, max: 10000, etiqueta: 'concentración (mg/ml)' },
  cadaH: { min: 0.5, max: 168, etiqueta: 'intervalo (h)' },
  mlh: { min: 0.1, max: 1000, etiqueta: 'ritmo de fluidos (ml/h)' },
  perdidasMlDia: { min: 0, max: 20000, etiqueta: 'pérdidas (ml/día)' },
  pctDeshidratacion: { min: 0, max: 15, etiqueta: 'deshidratación (%)' },
  mg: { min: 0.001, max: 100000, etiqueta: 'dosis (mg)' },
  ml: { min: 0.001, max: 10000, etiqueta: 'dosis (ml)' },
  dias: { min: 1, max: 365, etiqueta: 'duración (días)' },
};

/**
 * Resolución de medición por defecto (clínica).
 * Documentado: si el volumen calculado es >0 y < RESOLUCION_MEDICION_ML,
 * NO se autoriza la administración sin revisión documentada (quién + motivo).
 * No se modifica la dosis ni se sugiere dilución automáticamente.
 * Una clínica puede pasar otra resolución a formatearCantidadDosis / puedeAdministrar / dosisDe.
 */
/** Resolución de medición configurable (ml). Por defecto 0,01 ml.
 *  Sustituible por ajuste de clínica sin tocar fórmulas: pasar `resolucionMl` a dosisDe / puedeAdministrar.
 *  Volúmenes < resolución → no auto-autorizar; exigir revisión documentada. Nunca auto-diluir ni alterar la dosis. */
export const RESOLUCION_MEDICION_ML = 0.01;
/** Resolución de visualización mg (no altera el valor almacenado). */
export const RESOLUCION_MEDICION_MG = 0.01;

/** Especie con dosis orientativas en el vademécum. Otras: sin auto mg/kg. */
export const ESPECIES_VADEMECUM = ['perro', 'gato'];
export const especieSoportaMgkg = (esp) => ESPECIES_VADEMECUM.includes(esp);

/** Parseo estricto: acepta coma decimal ('4,5'); rechaza NaN, Infinity y texto parcial ('12abc'). */
export function parseNum(v) {
  if (v == null) return null;
  const s = String(v).trim().replace(/\s/g, '').replace(',', '.');
  if (s === '') return null;
  if (!/^[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/.test(s)) return null;
  const x = Number(s);
  return Number.isFinite(x) ? x : null;
}

/** true si el valor es un número finito usable (no NaN/Infinity). */
export function esCantidadFinita(v) {
  return typeof v === 'number' && Number.isFinite(v);
}

function rangoPeso(especie) {
  return PESO_RANGOS[especie] || PESO_RANGOS.otro;
}

function fmtKg(n) {
  return String(n).replace('.', ',');
}

/**
 * Valida un peso. Nunca corrige el valor: ok=false + error visible.
 * @returns {{ ok: true, valor: number } | { ok: false, error: string, codigo: string, valor?: number }}
 */
export function validarPeso(raw, especie = 'perro') {
  if (raw == null || String(raw).trim() === '') {
    return { ok: false, error: 'El peso es obligatorio', codigo: 'vacio' };
  }
  const x = parseNum(raw);
  if (x == null) {
    return { ok: false, error: 'El peso debe ser un número (ej. 4,5)', codigo: 'nan' };
  }
  if (x < 0) {
    return { ok: false, error: 'El peso no puede ser negativo', codigo: 'negativo', valor: x };
  }
  if (x === 0) {
    return { ok: false, error: 'El peso debe ser mayor que 0', codigo: 'cero', valor: x };
  }
  const r = rangoPeso(especie);
  const espTxt = especie === 'gato' ? 'gato' : especie === 'otro' ? 'esta especie' : 'perro';
  if (x < r.min || x > r.max) {
    return {
      ok: false,
      error: `Peso fuera de rango para ${espTxt} (${fmtKg(r.min)}–${fmtKg(r.max)} kg)`,
      codigo: 'rango',
      valor: x,
    };
  }
  return { ok: true, valor: x };
}

/** true solo si el peso se puede usar para calcular dosis / fluidos. */
export function pesoUsable(raw, especie = 'perro') {
  return validarPeso(raw, especie).ok;
}

/**
 * Teléfono opcional: vacío OK.
 * Solo dígitos, espacios, +, -, paréntesis y puntos; 9–15 dígitos.
 */
export function validarTelefono(raw) {
  const s = String(raw ?? '').trim();
  if (!s) return { ok: true, valor: '' };
  if (!/^[0-9+\-\s().]+$/.test(s)) {
    return { ok: false, error: 'El teléfono solo puede tener dígitos, espacios, +, -, ( ) y puntos' };
  }
  const digitos = s.replace(/\D/g, '');
  if (digitos.length < 9 || digitos.length > 15) {
    return { ok: false, error: 'Teléfono no válido (9–15 dígitos)' };
  }
  return { ok: true, valor: s };
}

function validarPositivoEnRango(raw, { min, max, etiqueta }, { obligatorio = false, permitirVacio = !obligatorio } = {}) {
  if (raw == null || String(raw).trim() === '') {
    if (permitirVacio) return { ok: true, valor: null };
    return { ok: false, error: `${etiqueta} es obligatorio` };
  }
  const x = parseNum(raw);
  if (x == null) return { ok: false, error: `${etiqueta} debe ser un número finito` };
  if (!Number.isFinite(x)) return { ok: false, error: `${etiqueta} no es un número válido` };
  if (x < 0) return { ok: false, error: `${etiqueta} no puede ser negativo`, valor: x };
  if (x === 0 && min > 0) return { ok: false, error: `${etiqueta} debe ser mayor que 0`, valor: x };
  if (x < min || x > max) {
    return { ok: false, error: `${etiqueta} fuera de rango (${fmtKg(min)}–${fmtKg(max)})`, valor: x };
  }
  return { ok: true, valor: x };
}

export const validarMgkg = (raw, opts) => validarPositivoEnRango(raw, LIMITES_HOSP.mgkg, opts);
export const validarConc = (raw, opts) => validarPositivoEnRango(raw, LIMITES_HOSP.conc, opts);
export const validarCadaH = (raw, opts) => validarPositivoEnRango(raw, LIMITES_HOSP.cadaH, opts);
export const validarMlh = (raw, opts) => validarPositivoEnRango(raw, LIMITES_HOSP.mlh, opts);
export const validarPerdidas = (raw, opts) => validarPositivoEnRango(raw, LIMITES_HOSP.perdidasMlDia, { permitirVacio: true, ...opts });
export const validarMg = (raw, opts) => validarPositivoEnRango(raw, LIMITES_HOSP.mg, opts);
export const validarMl = (raw, opts) => validarPositivoEnRango(raw, LIMITES_HOSP.ml, opts);

/** Duración en días enteros positivos: vacío = sin límite; 1–365.
 * Sistema documentado: solo días naturales enteros (no fracciones como 1,5).
 */
export function validarDuracionDias(raw) {
  if (raw == null || String(raw).trim() === '') return { ok: true, valor: null };
  const x = parseNum(raw);
  if (x == null) return { ok: false, error: 'La duración debe ser un número entero de días' };
  if (!Number.isFinite(x)) return { ok: false, error: 'La duración no es un número válido', valor: x };
  if (x < 0) return { ok: false, error: 'La duración no puede ser negativa', valor: x };
  if (x === 0) return { ok: false, error: 'La duración debe ser al menos 1 día', valor: x };
  if (!Number.isInteger(x)) {
    return { ok: false, error: 'La duración debe ser un número entero de días (sin decimales)', valor: x };
  }
  if (x < LIMITES_HOSP.dias.min || x > LIMITES_HOSP.dias.max) {
    return { ok: false, error: `Duración fuera de rango (1–${LIMITES_HOSP.dias.max} días)`, valor: x };
  }
  return { ok: true, valor: x };
}

/** Fin no anterior al inicio (ISO o Date). */
export function validarFinTrasInicio(inicio, fin) {
  if (!inicio || !fin) return { ok: true };
  const a = new Date(inicio).getTime();
  const b = new Date(fin).getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b)) {
    return { ok: false, error: 'Fechas de tratamiento no válidas' };
  }
  if (b < a) return { ok: false, error: 'La fecha de fin no puede ser anterior al inicio' };
  return { ok: true };
}

/** Valida parámetros de fluidoterapia (ml/h > 0 al guardar ritmo; 0 = parar se gestiona aparte). */
export function validarFluidos({ mlh, perdidas, pct } = {}) {
  const errores = [];
  if (mlh != null && String(mlh).trim() !== '') {
    const v = validarMlh(mlh, { permitirVacio: false });
    if (!v.ok) errores.push(v.error);
  }
  if (perdidas != null && String(perdidas).trim() !== '') {
    const v = validarPerdidas(perdidas);
    if (!v.ok) errores.push(v.error);
  }
  if (pct != null && pct !== '') {
    const x = parseNum(pct);
    if (x == null || x < LIMITES_HOSP.pctDeshidratacion.min || x > LIMITES_HOSP.pctDeshidratacion.max) {
      errores.push('Deshidratación (%) fuera de rango (0–15)');
    }
  }
  return errores.length ? { ok: false, error: errores.join('. ') } : { ok: true };
}

/**
 * Parsea fracciones simples ('1/2', '½') o número con coma.
 */
function parseFracOrNum(raw) {
  const s = String(raw ?? '').trim().replace(/½/g, '1/2').replace(/¼/g, '1/4').replace(/¾/g, '3/4');
  const frac = s.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (frac) {
    const a = Number(frac[1]);
    const b = Number(frac[2]);
    if (!b || !Number.isFinite(a) || !Number.isFinite(b)) return null;
    const x = a / b;
    return Number.isFinite(x) ? x : null;
  }
  return parseNum(s);
}

/**
 * Analiza dosis fija en texto libre.
 * - Una sola cantidad + unidad inequívoca (p. ej. «1 ml», «½ comp», «2 gotas») → ok.
 * - Cantidad mg/ml ≤ 0 → inválida.
 * - Varias cantidades, contradicciones («1 ml y -2 ml», «1 ml o 2 ml») o basura
 *   tras la dosis («5 ml cualquier cosa») → ambigua / rechazada.
 * Documentado: nunca se asume que un texto raro sea una dosis administrable.
 */
const UNIDADES_DOSIS = 'ml|mg|comp(?:rimidos?)?|gotas?|c[aá]ps(?:ulas?)?|ui|u\\.?i\\.?|µg|ug|mcg|g|cc';
const QTY_DOSIS = '([+-]?\\d+(?:[.,]\\d+)?|\\d+\\s*\\/\\s*\\d+)';

export function analizarDosisTexto(raw) {
  const s0 = String(raw ?? '').trim();
  if (!s0) return { ok: false, vacia: true, error: 'Falta la dosis' };
  const s = s0.replace(/½/g, '1/2').replace(/¼/g, '1/4').replace(/¾/g, '3/4');

  const rePair = new RegExp(`${QTY_DOSIS}\\s*(${UNIDADES_DOSIS})\\b`, 'gi');
  const pairs = [...s.matchAll(rePair)];
  if (pairs.length > 1) {
    return {
      ok: false, ambigua: true, contradictoria: true,
      error: 'Dosis contradictoria o ambigua (varias cantidades). Indica una sola (p. ej. «1 ml» o «½ comp»).',
    };
  }
  if (/\b(y|o|ó)\b/i.test(s) && pairs.length >= 1) {
    // Conector con otra cantidad implícita / alternativa
    const after = s.slice(pairs[0].index + pairs[0][0].length);
    if (/\d/.test(after) || /\b(y|o|ó)\b/i.test(s.slice(0, pairs[0].index) + after)) {
      return {
        ok: false, ambigua: true, contradictoria: true,
        error: 'Dosis contradictoria o alternativa («… y …» / «… o …»). Indica una sola cantidad.',
      };
    }
  }

  const reFull = new RegExp(`^${QTY_DOSIS}\\s*(${UNIDADES_DOSIS})\\.?$`, 'i');
  const m = s.match(reFull);
  if (!m) {
    if (pairs.length === 1) {
      const rest = s.slice(pairs[0].index + pairs[0][0].length).trim().replace(/^\.+/, '').trim();
      if (rest) {
        return {
          ok: false, ambigua: true,
          error: 'Dosis con texto sobrante o ambiguo. Usa solo cantidad y unidad (p. ej. «5 ml», «2 gotas»).',
        };
      }
    }
    const bare = s.match(/^([+-]?\d+(?:[.,]\d+)?|\d+\s*\/\s*\d+)\s*$/);
    if (bare) {
      const qty = parseFracOrNum(bare[1]);
      if (qty != null && !(qty > 0)) {
        return { ok: false, invalida: true, cantidad: qty, error: 'La dosis no puede ser negativa ni cero' };
      }
      return {
        ok: false, ambigua: true, cantidad: qty,
        error: 'Dosis ambigua: indica la unidad (ml, mg, comp, gotas…). No se interpreta automáticamente.',
      };
    }
    return {
      ok: false, ambigua: true,
      error: 'Dosis ambigua: no se interpreta automáticamente. Clarifica cantidad y unidad (p. ej. «1 ml», «½ comp»).',
    };
  }

  const qty = parseFracOrNum(m[1]);
  const unidad = m[2].toLowerCase();
  if (qty == null || !Number.isFinite(qty)) {
    return { ok: false, ambigua: true, error: 'Cantidad de dosis no interpretable', unidad };
  }
  if (!(qty > 0)) {
    return {
      ok: false, invalida: true, cantidad: qty, unidad,
      error: unidad === 'ml' || unidad === 'mg'
        ? `La dosis en ${unidad} no puede ser negativa ni cero`
        : 'La cantidad de la dosis debe ser mayor que 0',
    };
  }
  const explicitaMgMl = unidad === 'ml' || unidad === 'mg';
  return {
    ok: true, cantidad: qty, unidad,
    explicitaMgMl, otraUnidad: !explicitaMgMl,
  };
}

/** Presenta un número positivo sin convertirlo en «0» por redondeo. */
export function formatearNumeroDosis(n) {
  if (n == null || !Number.isFinite(n) || !(n > 0)) return '';
  if (n >= 1) return n.toLocaleString('es-ES', { maximumFractionDigits: 2 });
  // Valores pequeños: suficientes decimales para no mostrar 0
  let s = n.toLocaleString('es-ES', { maximumFractionDigits: 6, minimumFractionDigits: 0 });
  const parsed = parseNum(s);
  if (parsed === 0 || s === '0') {
    s = n.toExponential(2).replace('.', ',');
  }
  return s;
}

/**
 * Formato de visualización separado del valor numérico exacto.
 * Nunca muestra un volumen/masa positivo como «0 ml»/«0 mg».
 * No redondea a un volumen administrable ni sugiere dilución.
 * Si 0 < n < resolución → bajoResolucion + aviso de revisión documentada.
 */
export function formatearCantidadDosis(n, unidad, { resolucion } = {}) {
  if (n == null || !Number.isFinite(n)) return { txt: '', aviso: null, valorExacto: null };
  if (!(n > 0)) return { txt: '', aviso: 'cantidad no positiva', valorExacto: n, invalida: true };
  const res = resolucion ?? (unidad === 'ml' ? RESOLUCION_MEDICION_ML : RESOLUCION_MEDICION_MG);
  const numTxt = formatearNumeroDosis(n);
  const txt = `${numTxt} ${unidad}`;
  if (n < res) {
    const resTxt = String(res).replace('.', ',');
    // ml: resolución de medición configurada (no es un límite clínico universal).
    // mg: aviso clínico/visual distinto; NO implica revisión de volumen.
    const aviso = unidad === 'ml'
      ? `Volumen ${numTxt} ml por debajo de la resolución de medición configurada (${resTxt} ml): requiere revisión documentada antes de administrar. No es un límite clínico universal; no se modifica la dosis automáticamente.`
      : `Dosis ${numTxt} ${unidad} muy pequeña (umbral de visualización ${resTxt} ${unidad}): revisar clínicamente. No implica un volumen en ml ni un límite de administración universal.`;
    return {
      txt, aviso, valorExacto: n, demasiadoPequeno: true,
      // Solo volumen ml marca bajoResolucion (gate de administración).
      bajoResolucion: unidad === 'ml',
      resolucion: res,
      unidad,
    };
  }
  return { txt, aviso: null, valorExacto: n, displayRedondeado: n, resolucion: res, unidad };
}

/** Une mg y ml en un texto de hoja sin perder precisión ni mostrar ceros falsos. */
export function textoDosisCalculada({ mg, ml, resolucionMl = RESOLUCION_MEDICION_ML, resolucionMg = RESOLUCION_MEDICION_MG } = {}) {
  const parts = [];
  let avisoMg = null;
  let avisoMl = null;
  let bajoResolucion = false; // solo volumen ml real
  let demasiadoPequeno = false;
  let fmtMg = null;
  let fmtMl = null;
  if (mg != null && Number.isFinite(mg) && mg > 0) {
    fmtMg = formatearCantidadDosis(mg, 'mg', { resolucion: resolucionMg });
    parts.push(fmtMg.txt);
    if (fmtMg.aviso) avisoMg = fmtMg.aviso;
    if (fmtMg.demasiadoPequeno) demasiadoPequeno = true;
  }
  if (ml != null && Number.isFinite(ml) && ml > 0) {
    fmtMl = formatearCantidadDosis(ml, 'ml', { resolucion: resolucionMl });
    parts.push(fmtMl.txt);
    if (fmtMl.aviso) avisoMl = fmtMl.aviso;
    if (fmtMl.bajoResolucion) { bajoResolucion = true; demasiadoPequeno = true; }
  }
  // Preferir aviso de volumen si existe; si no hay ml, solo aviso de mg (nunca mensaje de «0,01 ml»).
  const aviso = avisoMl || avisoMg || null;
  return {
    txt: parts.join(' = '),
    txtMg: fmtMg?.txt || '',
    txtMl: fmtMl?.txt || '',
    aviso,
    avisoMg,
    avisoMl,
    bajoResolucion,
    demasiadoPequeno,
    fmtMg,
    fmtMl,
  };
}

/**
 * Clasifica la prescripción: calculada por mg/kg vs fija (mg/ml/texto).
 * UI debe etiquetar claramente ambos modos.
 */
export function tipoPrescripcion(t) {
  if (t?.mgkg != null && String(t.mgkg).trim() !== '') return 'mgkg';
  return 'fija';
}

export function etiquetaPrescripcion(modo) {
  return modo === 'mgkg' ? 'Calculada (mg/kg × peso)' : 'Fija (no depende del peso)';
}

/**
 * ¿Se puede registrar una administración «dada» con esta dosis calculada?
 * Cuidados (sin fármaco) siempre sí. Omisiones no usan esta función.
 */
export function puedeAdministrar(d, {
  esCuidado = false,
  revision = null,
  resolucionMl = RESOLUCION_MEDICION_ML,
} = {}) {
  if (esCuidado) return { ok: true };
  if (!d) return { ok: false, error: 'No hay dosis calculada' };
  if (d.sinPeso) return { ok: false, error: d.mensaje || 'Falta el peso: no se puede administrar' };
  if (d.pesoInvalido || d.paramInvalido || d.invalida) {
    return { ok: false, error: d.mensaje || 'Dosis no válida: no se puede administrar' };
  }
  if (d.ambigua) {
    return { ok: false, error: d.mensaje || 'Dosis ambigua: clarifica cantidad y unidad antes de administrar', ambigua: true };
  }
  if (!d.txt || !String(d.txt).trim()) {
    return { ok: false, error: 'No hay dosis que administrar (indica mg/kg o una dosis fija)' };
  }
  if (d.mensaje && d.txt === d.mensaje) return { ok: false, error: d.mensaje };
  if (d.mg != null && !(d.mg > 0)) return { ok: false, error: 'La dosis en mg no puede ser negativa ni cero' };
  if (d.ml != null && !(d.ml > 0)) return { ok: false, error: 'La dosis en ml no puede ser negativa ni cero' };

  // Solo volumen ml real por debajo de la resolución de medición configurada.
  // Dosis mg pequeña sin ml calculado: aviso clínico, NO exige revisión de volumen.
  const bajoVol = d.ml != null && Number.isFinite(d.ml) && d.ml > 0 && d.ml < resolucionMl;
  if (bajoVol) {
    const revOk = !!(revision
      && revision.confirmado
      && String(revision.revisadoPor || '').trim()
      && String(revision.motivo || '').trim());
    const resTxt = String(resolucionMl).replace('.', ',');
    const msg = (d.avisoMl || d.aviso || '').includes('ml')
      ? (d.avisoMl || d.aviso)
      : `Volumen ${String(d.ml).replace('.', ',')} ml por debajo de la resolución de medición configurada (${resTxt} ml): marca revisión documentada (quién y motivo). No es un límite clínico universal.`;
    if (!revOk) {
      return {
        ok: false,
        requiereRevision: true,
        resolucionMl,
        error: msg,
      };
    }
    return {
      ok: true,
      requiereRevision: true,
      revisionDocumentada: true,
      aviso: msg,
      resolucionMl,
    };
  }
  // mg pequeña u otros avisos: no bloquean administración por resolución de volumen
  return { ok: true, aviso: d.aviso || null };
}

/**
 * Construye el registro de toma. Distingue administración vs omisión.
 * - estado 'dado': exige dosis administrable; guarda d.txt (nunca el mensaje de error).
 * - estado 'omitido': guarda motivo; NO guarda dosis administrada.
 */
export function construirRegistroToma({
  t, slot, estado, motivo = '', cuando, por = '', peso, especie = 'perro', prn = false,
  revision = null, resolucionMl = RESOLUCION_MEDICION_ML,
}) {
  const dada = cuando ? new Date(cuando).toISOString() : new Date().toISOString();
  const base = { tid: t.id, slot, dada, por, ...(prn ? { prn: true } : {}) };
  if (estado === 'omitido') {
    return {
      ok: true,
      registro: {
        ...base,
        estado: 'omitido',
        motivo: (motivo && String(motivo).trim()) || 'sin motivo',
      },
    };
  }
  if (estado !== 'dado') {
    return { ok: false, error: 'Estado de toma no reconocido' };
  }
  const esCuidado = t.cat === 'cuidado';
  const d = dosisDe(t, peso, especie, { resolucionMl });
  const adm = puedeAdministrar(d, { esCuidado, revision, resolucionMl });
  if (!adm.ok) return { ok: false, error: adm.error, requiereRevision: !!adm.requiereRevision };
  const rev = adm.revisionDocumentada ? {
    revisionBajoResolucion: true,
    revisadoPor: String(revision.revisadoPor).trim(),
    revisionMotivo: String(revision.motivo).trim(),
    resolucionMl,
  } : {};
  return {
    ok: true,
    registro: {
      ...base,
      estado: 'dado',
      ...(esCuidado ? {} : {
        dosis: d.txt,
        modoDosis: d.modo,
        ...(d.mg != null ? { dosisMg: d.mg } : {}),
        ...(d.ml != null ? { dosisMl: d.ml } : {}),
        ...rev,
      }),
    },
  };
}

// Horas programadas de un tratamiento entre dos instantes (ms)
export function slots(t, desde, hasta) {
  const tipo = t.tipo || 'pauta';
  if (tipo === 'unica') { const x = new Date(t.inicio).getTime(); return x >= desde && x < hasta ? [new Date(x).toISOString()] : []; }
  if (tipo !== 'pauta' || !(t.cadaH > 0)) return [];
  const ini = new Date(t.inicio).getTime();
  const fin = Math.min(hasta, t.fin ? new Date(t.fin).getTime() : Infinity, t.suspendido ? new Date(t.suspendido).getTime() : Infinity);
  const out = [];
  let k = Math.max(0, Math.ceil((desde - ini) / (t.cadaH * H)));
  for (let s = ini + k * t.cadaH * H; s < fin && out.length < 200; k++, s = ini + k * t.cadaH * H) if (s >= desde) out.push(new Date(s).toISOString());
  return out;
}

// Estado de una toma programada
export function estadoSlot(slot, tomas, ahora = Date.now(), tid = null) {
  const r = (tomas || []).find((x) => x.slot === slot && (!tid || x.tid === tid));
  if (r) return { estado: r.estado || 'dado', registro: r };
  const m = (new Date(slot).getTime() - ahora) / 6e4;
  return { estado: m < -30 ? 'atrasada' : m <= 60 ? 'ya' : 'futura' };
}

// Tomas que necesitan acción ahora (para avisos y contadores): de las últimas 12 h hasta dentro de 1 h
export function tomasPendientes(h, ahora = Date.now()) {
  const out = [];
  for (const t of h.tratamientos || []) {
    if (t.activo === false) continue;
    for (const s of slots(t, ahora - 12 * H, ahora + H)) {
      const e = estadoSlot(s, h.tomas, ahora, t.id);
      if (e.estado === 'ya' || e.estado === 'atrasada') out.push({ t, slot: s, estado: e.estado });
    }
  }
  return out.sort((a, b) => a.slot.localeCompare(b.slot));
}

export function proximaDe(h, ahora = Date.now()) {
  let mejor = null;
  for (const t of h.tratamientos || []) {
    if (t.activo === false) continue;
    for (const s of slots(t, ahora - 12 * H, ahora + 36 * H)) {
      if (['dado', 'omitido'].includes(estadoSlot(s, h.tomas, ahora, t.id).estado)) continue;
      if (!mejor || s < mejor.slot) mejor = { t, slot: s };
      break;
    }
  }
  return mejor;
}

/**
 * Fluidoterapia: distingue volumen YA administrado (últimas 24 h) del PREVISTO
 * (próximas 24 h al ritmo actual). La fórmula clínica de mantenimiento
 * (132·kg^0,75 / 80·kg^0,75) no se altera; solo se etiquetan mejor los totales.
 * - mlAdministrados24h / ml24: integral del ritmo histórico hasta `ahora`.
 * - mlPrevistos24h: ritmo_actual × 24 (si hay ritmo activo > 0).
 */
export function fluidos(h, ahora = Date.now()) {
  const l = [...(h.fluidos || [])].sort((a, b) => a.desde.localeCompare(b.desde));
  const actual = l.length ? l[l.length - 1] : null;
  let ml = 0;
  for (let i = 0; i < l.length; i++) {
    const a = Math.max(new Date(l[i].desde).getTime(), ahora - 24 * H);
    const b = i + 1 < l.length ? new Date(l[i + 1].desde).getTime() : ahora;
    const rate = Number(l[i].mlh);
    // Registros guardados con ml/h inválido: no aportan volumen (no se mutan).
    if (b > a && Number.isFinite(rate) && rate > 0) ml += rate * ((b - a) / H);
  }
  const rateAct = actual ? Number(actual.mlh) : NaN;
  const activo = actual && Number.isFinite(rateAct) && rateAct > 0 ? actual : null;
  const mlAdministrados24h = Math.round(ml);
  const mlPrevistos24h = activo ? Math.round(rateAct * 24) : null;
  return {
    actual: activo,
    ml24: mlAdministrados24h, // alias histórico = administrados
    mlAdministrados24h,
    mlPrevistos24h,
  };
}

// Mantenimiento orientativo: perro 132·kg^0,75, gato 80·kg^0,75 ml/día
export const mantenimiento = (esp, kg) => {
  const v = validarPeso(kg, esp === 'gato' ? 'gato' : esp === 'otro' ? 'otro' : 'perro');
  return v.ok ? Math.round(((esp === 'gato' ? 80 : 132) * v.valor ** 0.75) / 24) : null;
};

// Sugerencia desde el vademécum: dosis mg/kg, cada cuánto, vía y concentración inyectable/líquida.
// Elección documentada: solo perro/gato tienen dosis mg/kg del vademécum.
// Especie «otro» (u otra no cubierta): NO se reutiliza la dosis de perro; sin auto mg/kg.
// El veterinario puede pautar solo una dosis fija explícita (texto / mg / ml).
// validados: Set de ids de fármacos del vademécum que la clínica ha marcado como validados (null = sin control, solo pruebas).
// Un fármaco del vademécum SIN validar no da dosis para pacientes: ni se rellena solo ni sale en «Calcular dosis».
export function sugerencia(nombre, esp, propios = [], validados = null) {
  const n = String(nombre || '').trim().toLowerCase();
  const p = propios.find((x) => (x.nombre || '').toLowerCase() === n);
  const f = FARMACOS.find((x) => x.nombre.toLowerCase() === n);
  const espOk = especieSoportaMgkg(esp);
  if (p) {
    if (!espOk) {
      return {
        propio: p, f, mgkg: null, mgkgMax: null, unidad: null, cadaH: p.cadaH ?? null, via: p.via,
        conc: null, concIny: null, especieSinVademecum: true,
        aviso: 'Esta especie no tiene dosis mg/kg en el vademécum: usa solo una dosis fija explícita.',
      };
    }
    const mgkg = p.mgkg?.[esp] ?? p.mgkg?.perro ?? null;
    return { propio: p, f, mgkg, mgkgMax: mgkg, unidad: 'mg/kg', cadaH: p.cadaH ?? null, via: p.via, conc: p.conc ?? null, concIny: p.conc ?? null };
  }
  if (!f) return null;
  if (validados && !validados.has(idFarmaco(f.nombre))) {
    return {
      f, mgkg: null, mgkgMax: null, unidad: null, cadaH: null, via: null, conc: null, concIny: null, sinValidar: true,
      aviso: 'La dosis de este fármaco en el vademécum aún no está validada por la clínica: no se rellena sola. Indica la dosis tú o valídala en el Vademécum.',
    };
  }
  if (!espOk) {
    return {
      f, mgkg: null, mgkgMax: null, unidad: null, cadaH: null, via: null, conc: null, concIny: null,
      especieSinVademecum: true,
      aviso: 'Especie no cubierta por el vademécum: no se calcula mg/kg. Indica una dosis fija (p. ej. «1 ml» o «½ comp»).',
    };
  }
  const d = f.d?.[esp] || f.d?.perro;
  if (!d) return { f };
  const pres = presentaciones(f);
  const iny = pres.find((p) => p.forma === 'iny');
  const liq = pres.find((p) => p.forma === 'liq');
  return { f, mgkg: d[0], mgkgMax: d[1], unidad: d[2], cadaH: d[3], via: d[4], conc: (iny || liq)?.valor || null, concIny: iny?.valor || null };
}

export const fueraDeRango = (esp, campo, v) => {
  const r = RANGOS[esp]?.[campo]; const x = parseFloat(String(v ?? '').replace(',', '.'));
  return r && !isNaN(x) ? (x < r[0] ? 'bajo' : x > r[1] ? 'alto' : null) : null;
};

export { FARMACOS };

// Dosis de un tratamiento con el peso ACTUAL: si se pautó en mg/kg, se recalcula sola al cambiar el peso.
// Si el peso o los parámetros son inválidos NO se presentan números: mensaje claro (nunca como «dosis»).
// modo: 'mgkg' | 'fija' — para etiquetar en UI y en el registro.
// mg/ml numéricos: precisión completa en `mg`/`ml`; `txt` es solo visualización (formatearCantidadDosis).
export function dosisDe(t, peso, especie = 'perro', { resolucionMl = RESOLUCION_MEDICION_ML, resolucionMg = RESOLUCION_MEDICION_MG } = {}) {
  const vacio = peso == null || String(peso).trim() === '';
  const modo = tipoPrescripcion(t);

  if (modo === 'mgkg') {
    if (!especieSoportaMgkg(especie)) {
      return {
        mg: null, ml: null, txt: '', sinPeso: false, pesoInvalido: false, paramInvalido: true, invalida: true,
        modo, mensaje: 'Especie sin vademécum: no se calcula mg/kg. Usa una dosis fija.',
      };
    }
    if (vacio) {
      return { mg: null, ml: null, txt: '', sinPeso: true, pesoInvalido: false, modo, mensaje: 'falta el peso' };
    }
    const vp = validarPeso(peso, especie);
    if (!vp.ok) {
      return { mg: null, ml: null, txt: '', sinPeso: false, pesoInvalido: true, modo, mensaje: vp.error || 'Peso no válido: no se calcula la dosis' };
    }
    const mgkgOk = validarMgkg(t.mgkg, { permitirVacio: false });
    if (!mgkgOk.ok) {
      return { mg: null, ml: null, txt: '', sinPeso: false, pesoInvalido: false, paramInvalido: true, invalida: true, modo, mensaje: mgkgOk.error };
    }
    const mg = mgkgOk.valor * vp.valor;
    if (!Number.isFinite(mg) || mg <= 0) {
      return { mg: null, ml: null, txt: '', sinPeso: false, paramInvalido: true, invalida: true, modo, mensaje: 'La dosis calculada no es válida' };
    }
    let ml = null;
    const concRaw = t.conc;
    if (concRaw != null && String(concRaw).trim() !== '') {
      const vc = validarConc(concRaw, { permitirVacio: false });
      if (!vc.ok) {
        return { mg, ml: null, txt: '', sinPeso: false, pesoInvalido: false, paramInvalido: true, invalida: true, modo, mensaje: vc.error };
      }
      ml = mg / vc.valor;
      if (!Number.isFinite(ml) || ml <= 0) {
        return { mg, ml: null, txt: '', paramInvalido: true, invalida: true, modo, mensaje: 'El volumen calculado no es válido' };
      }
    }
    const composed = textoDosisCalculada({ mg, ml, resolucionMl, resolucionMg });
    return {
      mg, ml, txt: composed.txt, txtMg: composed.txtMg, txtMl: composed.txtMl,
      sinPeso: false, pesoInvalido: false, modo,
      demasiadoPequeno: !!composed.demasiadoPequeno, bajoResolucion: !!composed.bajoResolucion,
      aviso: composed.aviso || null, avisoMg: composed.avisoMg || null, avisoMl: composed.avisoMl || null, resolucionMl,
    };
  }

  // Dosis fija (mg / ml / texto): no depende del peso. Rechaza mg/ml negativos o no finitos.
  const hasMg = t.mg != null && String(t.mg).trim() !== '';
  const hasMl = t.ml != null && String(t.ml).trim() !== '';
  let mg = null;
  let ml = null;
  if (hasMg) {
    const vm = validarMg(t.mg, { permitirVacio: false });
    if (!vm.ok) {
      return { mg: null, ml: null, txt: '', sinPeso: false, paramInvalido: true, invalida: true, modo: 'fija', mensaje: vm.error };
    }
    mg = vm.valor;
  }
  if (hasMl) {
    const vl = validarMl(t.ml, { permitirVacio: false });
    if (!vl.ok) {
      return { mg, ml: null, txt: '', sinPeso: false, paramInvalido: true, invalida: true, modo: 'fija', mensaje: vl.error };
    }
    ml = vl.valor;
  }
  if (ml != null || mg != null) {
    const composed = textoDosisCalculada({ mg, ml, resolucionMl, resolucionMg });
    return {
      mg, ml, txt: composed.txt, txtMg: composed.txtMg, txtMl: composed.txtMl,
      sinPeso: false, pesoInvalido: false, modo: 'fija',
      demasiadoPequeno: !!composed.demasiadoPequeno, bajoResolucion: !!composed.bajoResolucion,
      aviso: composed.aviso || null, avisoMg: composed.avisoMg || null, avisoMl: composed.avisoMl || null, resolucionMl,
    };
  }

  // Solo texto libre: validar cantidades explícitas no positivas; otras unidades OK; ambiguo ≠ seguro.
  const texto = String(t.dosis || '').trim();
  if (!texto) {
    return { mg: null, ml: null, txt: '', sinPeso: false, pesoInvalido: false, modo: 'fija', invalida: true, mensaje: 'No hay dosis definida' };
  }
  const an = analizarDosisTexto(texto);
  if (an.invalida) {
    return {
      mg: null, ml: null, txt: '', sinPeso: false, paramInvalido: true, invalida: true, modo: 'fija',
      mensaje: an.error,
    };
  }
  if (an.ambigua || !an.ok) {
    return {
      mg: null, ml: null, txt: texto, sinPeso: false, ambigua: true, modo: 'fija',
      mensaje: an.error || 'Dosis ambigua',
    };
  }
  return { mg: null, ml: null, txt: texto, sinPeso: false, pesoInvalido: false, modo: 'fija' };
}

// Motivo de ingreso por aparato (icono + color)
export const CATEGORIAS = {
  digestivo: ['🤢', 'Digestivo'], renal: ['💧', 'Renal / urinario'], cirugia: ['✂️', 'Cirugía / postoperatorio'], trauma: ['🩹', 'Traumatismo'],
  infeccioso: ['🦠', 'Infeccioso'], endocrino: ['🧪', 'Endocrino / metabólico'], cardio: ['🫀', 'Cardio-respiratorio'], neuro: ['🧠', 'Neurológico'],
  intox: ['☠️', 'Intoxicación'], hemato: ['🩸', 'Hematológico'], otro: ['🐾', 'Otro'],
};
// Cuidados y controles habituales (se programan como la medicación, sin dosis)
export const CUIDADOS = ['Pasear / sacar a orinar', 'Glucemia', 'Pesar', 'Dar de comer', 'Limpiar herida', 'Revisar vía / vendaje', 'Cambiar de postura', 'Medir diuresis', 'Hematocrito', 'Analítica de control', 'Llamar al dueño'];
export const ALTA_CHECK = [['informe', 'Informe de alta hecho'], ['casa', 'Medicación para casa preparada'], ['revision', 'Cita de revisión dada'], ['dueño', 'Dueño avisado de la recogida'], ['cobro', 'Cobrado']];

// Texto de cuánto falta para el alta prevista
export function altaTexto(fecha) {
  if (!fecha) return null;
  const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
  const d = Math.round((new Date(`${fecha}T00:00:00`) - hoy) / 864e5);
  return { d, txt: d < 0 ? `prevista hace ${-d} día${d === -1 ? '' : 's'}` : d === 0 ? 'HOY' : d === 1 ? 'mañana' : `en ${d} días` };
}
